import React, { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import FilterPanel from '../components/FilterPanel';
import CandidateTable from '../components/CandidateTable';
import CandidateModal from '../components/CandidateModal';
import Pagination from '../components/Pagination';
import './CandidateSearch.css';

const GET_CANDIDATES = gql`
  query GetCandidates($filters: FilterInput, $limit: Int, $offset: Int) {
    candidates(filters: $filters, limit: $limit, offset: $offset) {
      slug
      name
      age
      isTechnical
      isWoman
      ycFounderLabel
      avatarUrl
      linkedin
      education
      employment
      location
      country
      region
      timing
      emailSettings
      videoLink
      calendlyLink
      intro
      lifeStory
      freeTime
      other
      impressiveThing
      interests
      responsibilities
      companyName
      companyUrl
      companyDescription
      companyProgress
      fundingStatus
      hasIdea
      ideas
      hasCf
      currentCfLinkedin
      currentCfTechnical
      whyLookingForThirdCf
      reqFreeText
      equity
      cfHasIdea
      cfHasIdeaImportance
      cfIsTechnical
      cfIsTechnicalImportance
      cfResponsibilities
      cfResponsibilitiesImportance
      cfLocation
      cfLocationImportance
      cfLocationKmRange
      cfAgeMin
      cfAgeMax
      cfAgeImportance
      cfTimingImportance
      cfInterestsImportance
      cfIsFellowAlumniImportance
      cfIsYcAlumImportance
      cfIsWomanImportance
      lastSeenAt
      savedAt
      level
    }
    candidatesCount(filters: $filters)
  }
`;

const UPDATE_LEVEL_MUTATION = gql`
  mutation UpdateLevel($slug: String!, $level: Int!) {
    updateLevel(slug: $slug, level: $level) {
      slug
      level
    }
  }
`;

const AdminPanel = () => {
  const [filters, setFilters] = useState({ isAdmin: true });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const saved = localStorage.getItem('itemsPerPage');
    return saved ? parseInt(saved) : 25;
  });
  const [showFilters, setShowFilters] = useState(true);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [levelChanges, setLevelChanges] = useState({}); // Track local changes
  const [isSaving, setIsSaving] = useState(false);
  const [levelFilter, setLevelFilter] = useState({ min: '', max: '' }); // Level filter state
  const [tempLevelFilter, setTempLevelFilter] = useState({ min: '', max: '' }); // Temporary level filter for input
  const [levelFilterError, setLevelFilterError] = useState(''); // Error message for invalid input

  // Combine regular filters with level filter
  const combinedFilters = {
    ...filters,
    ...(levelFilter.min && { levelMin: parseInt(levelFilter.min) }),
    ...(levelFilter.max && { levelMax: parseInt(levelFilter.max) })
  };

  const { loading, error, data, refetch } = useQuery(GET_CANDIDATES, {
    variables: {
      filters: combinedFilters,
      limit: itemsPerPage,
      offset: (currentPage - 1) * itemsPerPage
    }
  });

  const [updateLevel] = useMutation(UPDATE_LEVEL_MUTATION);

  const handleFilterChange = (newFilters) => {
    const filtersWithAdmin = { ...newFilters, isAdmin: true };
    setFilters(filtersWithAdmin);
    setCurrentPage(1);
    const updatedCombinedFilters = {
      ...filtersWithAdmin,
      ...(levelFilter.min && { levelMin: parseInt(levelFilter.min) }),
      ...(levelFilter.max && { levelMax: parseInt(levelFilter.max) })
    };
    refetch({
      filters: updatedCombinedFilters,
      limit: itemsPerPage,
      offset: 0
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    refetch({
      filters: combinedFilters,
      limit: itemsPerPage,
      offset: (page - 1) * itemsPerPage
    });
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    localStorage.setItem('itemsPerPage', newItemsPerPage.toString());
    setCurrentPage(1);
    refetch({
      filters: combinedFilters,
      limit: newItemsPerPage,
      offset: 0
    });
  };

  const handleCandidateClick = (candidate) => {
    setSelectedCandidate(candidate);
  };

  const handleCloseModal = () => {
    setSelectedCandidate(null);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleLevelChange = (slug, level) => {
    // Store changes locally without saving to DB
    // Restrict input to 0-100000 range
    if (validateLevelInput(level)) {
      const levelValue = parseInt(level) || 0;
      setLevelChanges(prev => ({
        ...prev,
        [slug]: levelValue
      }));
    }
  };

  const applyLevelChanges = async () => {
    if (Object.keys(levelChanges).length === 0) {
      return; // No changes to apply
    }

    setIsSaving(true);
    try {
      // Apply all changes
      const promises = Object.entries(levelChanges).map(([slug, level]) =>
        updateLevel({
          variables: { slug, level }
        })
      );

      await Promise.all(promises);

      // Clear local changes and refetch data
      setLevelChanges({});
      refetch();
    } catch (err) {
      console.error('Failed to update levels:', err);
      alert('Failed to save levels. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const cancelChanges = () => {
    setLevelChanges({});
  };

  const handleLevelUpdate = async (slug, level) => {
    // This is now just for local tracking
    handleLevelChange(slug, level);
  };

  const validateLevelInput = (value) => {
    // Allow empty string for clearing input
    if (value === '') return true;

    // Check if it's a valid number
    const num = parseInt(value);
    if (isNaN(num)) return false;

    // Check range
    return num >= 0 && num <= 100000;
  };

  const handleLevelFilterChange = (field, value) => {
    // Restrict input to 0-100000 range
    if (validateLevelInput(value)) {
      setTempLevelFilter(prev => ({ ...prev, [field]: value }));
      setLevelFilterError(''); // Clear error when valid input
    } else {
      // Show error for invalid input
      setLevelFilterError('Level must be between 0 and 100,000');
      setTimeout(() => setLevelFilterError(''), 2000); // Clear error after 2 seconds
    }
  };

  const applyLevelFilter = () => {
    setLevelFilter(tempLevelFilter);

    // Apply the filter
    const updatedCombinedFilters = {
      ...filters,
      ...(tempLevelFilter.min && { levelMin: parseInt(tempLevelFilter.min) }),
      ...(tempLevelFilter.max && { levelMax: parseInt(tempLevelFilter.max) })
    };

    setCurrentPage(1);
    refetch({
      filters: updatedCombinedFilters,
      limit: itemsPerPage,
      offset: 0
    });
  };

  const clearLevelFilter = () => {
    setLevelFilter({ min: '', max: '' });
    setTempLevelFilter({ min: '', max: '' });
    setCurrentPage(1);
    refetch({
      filters,
      limit: itemsPerPage,
      offset: 0
    });
  };

  if (error) return <div className="error">Error: {error.message}</div>;

  const totalPages = data ? Math.ceil(data.candidatesCount / itemsPerPage) : 0;
  const candidates = data?.candidates || [];

  // Merge candidates with local level changes
  const candidatesWithChanges = candidates.map(candidate => ({
    ...candidate,
    level: levelChanges.hasOwnProperty(candidate.slug)
      ? levelChanges[candidate.slug]
      : (candidate.level || 0)
  }));

  const hasChanges = Object.keys(levelChanges).length > 0;

  return (
    <div className="candidate-search admin-mode">
      {/* Level Control Modal - Fixed position top right */}
      {hasChanges && (
        <div className="level-control-modal">
          <div className="level-control-content">
            <span className="changes-indicator">
              {Object.keys(levelChanges).length} unsaved changes
            </span>
            <div className="level-control-buttons">
              <button
                className="level-cancel-btn"
                onClick={cancelChanges}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                className="level-apply-btn"
                onClick={applyLevelChanges}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Apply'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={`search-container ${showFilters ? 'filters-visible' : 'filters-hidden'}`}>
        <div className={`filter-sidebar ${showFilters ? 'visible' : 'hidden'}`}>
          <div className="filter-container">
            <FilterPanel
              onFilterChange={handleFilterChange}
              onToggleFilters={toggleFilters}
              showFilters={showFilters}
            />
          </div>
        </div>

        {!showFilters && (
          <button className="show-filters-btn" onClick={toggleFilters} title="Show Filters">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 17L11 12L6 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M13 17L18 12L13 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}

        <div className="results-section">
          <div className="results-content">
            {loading ? (
              <div className="loading">Loading candidates...</div>
            ) : (
              <CandidateTable
                candidates={candidatesWithChanges}
                onCandidateClick={handleCandidateClick}
                isAdmin={true}
                onLevelUpdate={handleLevelUpdate}
              />
            )}
          </div>
        </div>
      </div>

      {/* Footer Pagination */}
      <footer className="pagination-footer">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          totalItems={data?.candidatesCount || 0}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChange}
          loading={loading}
          isAdmin={true}
          levelFilter={levelFilter}
          tempLevelFilter={tempLevelFilter}
          onLevelFilterChange={handleLevelFilterChange}
          onApplyLevelFilter={applyLevelFilter}
          onClearLevelFilter={clearLevelFilter}
          levelFilterError={levelFilterError}
        />
      </footer>

      {selectedCandidate && (
        <CandidateModal
          candidate={selectedCandidate}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default AdminPanel;